import { useCallback, type ReactNode } from 'react';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';
import { supabase } from '@/integrations/supabase/client';
import { InactivityWarningDialog } from './InactivityWarningDialog';

interface InactivityTimeoutProviderProps {
 children: ReactNode;
}

/**
 * Inactivity Timeout Provider
 * 
 * Wraps the application to provide automatic logout after inactivity.
 * - Only activates when a user is logged in
 * - Shows warning dialog 5 minutes before logout
 * - Automatically logs out after 30 minutes of total inactivity
 */
export const InactivityTimeoutProvider = ({ children }: InactivityTimeoutProviderProps) => {
 const { user } = useSupabaseAuth();
 
 const handleLogout = useCallback(async () => {
 console.log('[InactivityTimeout] Logging out user due to inactivity');
 try {
 // Use local scope to ensure logout even if server is unavailable
 await supabase.auth.signOut({ scope: 'local' });
 } catch (error) {
 console.error('[InactivityTimeout] Error during sign out:', error);
 // Force redirect to auth page even if signOut fails
 window.location.href = '/auth';
 }
 }, []);

 const {
 showWarning,
 remainingSeconds,
 resetTimers,
 logout,
 } = useInactivityTimeout({
 enabled: !!user,
 onLogout: handleLogout,
 });

 const handleStayLoggedIn = useCallback(() => {
 console.log('[InactivityTimeout] User chose to stay logged in');
 resetTimers();
 }, [resetTimers]);

 const handleLogoutNow = useCallback(() => {
 console.log('[InactivityTimeout] User chose to logout now');
 logout();
 }, [logout]);

 return (
 <>
 {children}
 <InactivityWarningDialog
 open={showWarning}
 remainingSeconds={remainingSeconds}
 onStayLoggedIn={handleStayLoggedIn}
 onLogout={handleLogoutNow}
 />
 </>
 );
};
